import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === Number(id));

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <span className="w-16 h-16 mx-auto flex items-center justify-center text-foreground-300">
              <i className="ri-error-warning-line text-4xl"></i>
            </span>
            <h1 className="text-xl font-semibold text-foreground-900 mt-4">漫画未找到</h1>
            <p className="text-foreground-500 mt-2 text-sm">您访问的漫画可能已被移除或不存在</p>
            <Link
              to="/"
              className="inline-block mt-6 px-6 py-2 bg-primary-500 text-white text-sm rounded-full hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': `${comic.title} - 韩国漫画免费在线阅读`,
    'description': comic.description,
    'author': {
      '@type': 'Person',
      'name': comic.author,
    },
    'datePublished': '2026-01-01',
    'dateModified': comic.updateDate,
    'image': comic.coverImage,
    'inLanguage': 'zh-CN',
    'isPartOf': {
      '@type': 'WebSite',
      'name': '韩漫网站',
      'url': 'https://www.hanmanwangzhan.com/',
    },
    'publisher': {
      '@type': 'Organization',
      'name': '韩漫网站',
    },
  };

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': '首页',
        'item': 'https://www.hanmanwangzhan.com/',
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': `${comic.tags[0]}韩国漫画`,
        'item': `https://www.hanmanwangzhan.com/category/${comic.category}`,
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': comic.title,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbStructuredData) }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { label: `${comic.tags[0]}韩国漫画`, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />
        </div>

        {/* Comic Hero */}
        <section className="bg-background-100 border-y border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10">
            <div className="flex flex-col md:flex-row gap-8">
              <div className="flex-shrink-0 w-full sm:w-[240px] mx-auto md:mx-0">
                <div className="aspect-[2/3] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩国漫画`}
                    title={`${comic.title} - 韩国漫画免费在线阅读`}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-foreground-200 text-foreground-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.isHot && (
                    <span className="px-2.5 py-1 bg-accent-100 text-accent-700 rounded-full text-xs font-medium">
                      热门
                    </span>
                  )}
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 bg-secondary-100 text-secondary-800 rounded-full text-xs">
                      {tag}
                    </span>
                  ))}
                </div>

                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  {comic.title}
                </h1>
                <p className="text-foreground-600 text-sm mb-4">
                  作者：{comic.author}
                </p>

                <div className="flex items-center gap-6 mb-6">
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill"></i>
                    </span>
                    <span className="text-foreground-900 font-semibold text-sm">{comic.rating}</span>
                    <span className="text-foreground-400 text-xs">评分</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-eye-line"></i>
                    </span>
                    <span className="text-foreground-900 font-semibold text-sm">
                      {comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views}
                    </span>
                    <span className="text-foreground-400 text-xs">阅读</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                      <i className="ri-file-list-3-line"></i>
                    </span>
                    <span className="text-foreground-900 font-semibold text-sm">{comic.chapters.length}</span>
                    <span className="text-foreground-400 text-xs">章节</span>
                  </div>
                </div>

                <p className="text-foreground-700 text-sm leading-relaxed mb-6 line-clamp-4">
                  {comic.description}
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={`/comic/${comic.id}/read/1`}
                    className="px-8 py-3 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors duration-200 text-center whitespace-nowrap cursor-pointer"
                  >
                    开始阅读
                  </Link>
                  <button className="px-8 py-3 bg-background-200 text-foreground-700 text-sm font-semibold rounded-full hover:bg-background-300 transition-colors duration-200 whitespace-nowrap cursor-pointer">
                    加入收藏
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Chapters */}
        <section className="py-10 md:py-14">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <h2 className="text-xl font-bold text-foreground-900 mb-6">章节列表</h2>
            <div className="bg-background-100 rounded-lg border border-background-200/70 divide-y divide-background-200/70">
              {comic.chapters.map((chapter, index) => (
                <Link
                  key={chapter.id}
                  to={`/comic/${comic.id}/read/${chapter.id}`}
                  className="flex items-center justify-between px-4 md:px-6 py-4 hover:bg-background-50 transition-colors duration-200 cursor-pointer group"
                >
                  <div className="flex items-center gap-4">
                    <span className="text-foreground-400 text-sm font-mono w-8">{index + 1}</span>
                    <div>
                      <span className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors duration-200">
                        {chapter.title}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-foreground-400">{chapter.updateDate}</span>
                    <span className="w-5 h-5 flex items-center justify-center text-foreground-300 group-hover:text-primary-500 transition-colors duration-200">
                      <i className="ri-arrow-right-s-line"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6">
              <h2 className="text-xl font-bold text-foreground-900 mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}